import { Play, Clock, ImageOff, Heart, Check, CheckCircle2, FileText, Image as ImageIcon } from "lucide-react";
import { TID } from "@/constants/testIds";
import { useFavorites } from "@/contexts/FavoritesContext";
import { healAssetThumbnail } from "@/lib/healThumbnail";
import { isRecentlyTagged } from "@/lib/recentlyTagged";

function fmtDuration(sec) {
  if (!sec || sec < 1) return "—";
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export default function AssetCard({ asset, onClick, selectMode, selected, onToggleSelect }) {
  // Iter 43 — kind branching. Photos/PDFs are always "ready" (no encoding
  // pipeline), so the ready/processing gate collapses to a kind check.
  const kind = asset.kind || "video";
  const isVideo = kind === "video";
  const isPhoto = kind === "photo";
  const isPdf = kind === "pdf";
  const ready = asset.status === "ready";
  const processing = ["uploading", "processing"].includes(asset.status);
  const fav = useFavorites();
  const isFav = fav?.favSet?.has(asset.id);
  const justTagged = isRecentlyTagged(asset.last_tagged_at);

  const handleClick = (e) => {
    if (selectMode) {
      e.preventDefault();
      onToggleSelect?.(asset.id, e);
      return;
    }
    onClick?.();
  };

  return (
    <div
      data-testid={TID.assetCard(asset.id)}
      data-kind={kind}
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleClick(e); } }}
      className={`asset-card group cursor-pointer text-left bg-transparent border rounded-lg overflow-hidden hover:border-[var(--brand-accent)]/40 hover:shadow-[0_0_25px_rgba(245,166,35,0.06)] relative focus:outline-none focus:ring-2 focus:ring-[var(--brand-accent)]/60 ${selected ? "border-[var(--brand-accent)] shadow-[0_0_25px_rgba(245,166,35,0.18)]" : "border-zinc-800/40"}`}
    >
      {selectMode && (
        <button
          type="button"
          data-testid={TID.assetSelectCheckbox(asset.id)}
          onClick={(e) => { e.stopPropagation(); onToggleSelect?.(asset.id, e); }}
          className={`absolute top-2 left-2 z-10 w-6 h-6 rounded-md flex items-center justify-center transition border-2 ${selected ? "bg-[var(--brand-accent)] border-[var(--brand-accent)] text-black" : "bg-black/60 border-zinc-600 text-transparent hover:border-[var(--brand-accent)]"}`}
          aria-label={selected ? "Deselect" : "Select"}
        >
          <Check className="w-3.5 h-3.5" strokeWidth={3} />
        </button>
      )}
      {fav && !selectMode && (
        <button
          type="button"
          data-testid={TID.favoriteBtn(asset.id)}
          onClick={(e) => { e.stopPropagation(); fav.toggle(asset.id); }}
          title={isFav ? "Remove from favorites" : "Add to favorites"}
          className={`absolute top-2 z-10 w-7 h-7 rounded-full backdrop-blur-md flex items-center justify-center transition ${asset.version_number > 1 ? "right-10" : "right-2"} ${isFav ? "bg-[var(--brand-accent)]/90 text-black" : "bg-black/50 text-zinc-400 opacity-0 group-hover:opacity-100 hover:text-[var(--brand-accent)]"}`}
        >
          <Heart className={`w-3.5 h-3.5 ${isFav ? "fill-black" : ""}`} />
        </button>
      )}
      <div className="relative aspect-video overflow-hidden bg-zinc-900">
        {/* Iter 50 — Version chip. Only shows on assets with >1 version. */}
        {asset.version_number > 1 && (
          <div
            data-testid={`version-badge-${asset.id}`}
            className="absolute top-2 right-2 z-[5] bg-[var(--brand-accent)] text-black text-[10px] font-semibold px-1.5 py-0.5 rounded uppercase tracking-wider backdrop-blur-md flex items-center gap-0.5"
            title={`Latest is V${asset.version_number}`}
          >
            V{asset.version_number}
          </div>
        )}
        {isPdf && !asset.thumbnail_url ? (
          // PDF without a rasterized thumbnail — render the cinematic file icon panel.
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-br from-zinc-900 via-zinc-950 to-black text-zinc-500 gap-1.5">
            <FileText className="w-10 h-10 text-[var(--brand-accent)]/70" strokeWidth={1.5} />
            <span className="text-[9px] uppercase tracking-[0.2em] text-zinc-500">PDF</span>
          </div>
        ) : (ready && asset.thumbnail_url) ? (
          <img
            src={asset.thumbnail_url}
            alt={asset.title}
            className="thumb-img object-cover w-full h-full"
            onError={(e) => {
              // Only videos benefit from Stream thumbnail healing; photos come
              // straight from Bunny Storage and don't need re-signing.
              if (isVideo) {
                healAssetThumbnail(asset.id, (fresh) => { e.target.src = fresh; });
              }
              e.target.style.display = "none";
            }}
          />
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-zinc-700">
            {processing ? (
              <>
                <div className="spinner mb-2" />
                <span className="text-[10px] uppercase tracking-wider">
                  {asset.status === "uploading" ? "Uploading" : "Encoding"}
                </span>
              </>
            ) : asset.status === "failed" ? (
              <>
                <ImageOff className="w-6 h-6 mb-1" />
                <span className="text-[10px] uppercase tracking-wider text-red-400">Failed</span>
              </>
            ) : (
              isPhoto ? <ImageIcon className="w-6 h-6" /> : <ImageOff className="w-6 h-6" />
            )}
          </div>
        )}

        {/* Play badge — VIDEOS ONLY. Photos are static; PDFs open in a new tab. */}
        {isVideo && ready && (
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
            <div className="w-12 h-12 rounded-full bg-[var(--brand-accent)]/90 flex items-center justify-center">
              <Play className="w-5 h-5 text-black fill-black ml-0.5" />
            </div>
          </div>
        )}

        {/* Kind badge (photo/pdf only — videos already telegraph via play icon) */}
        {(isPhoto || isPdf) && (
          <div
            data-testid={`kind-badge-${asset.id}`}
            className="absolute bottom-2 right-2 bg-black/80 text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full backdrop-blur-md flex items-center gap-1 text-zinc-300"
          >
            {isPhoto ? <ImageIcon className="w-2.5 h-2.5" /> : <FileText className="w-2.5 h-2.5" />}
            {isPhoto ? "Photo" : "PDF"}
          </div>
        )}

        {isVideo && asset.duration_sec > 0 && (
          <div className="absolute bottom-2 right-2 bg-black/80 text-white text-[10px] px-1.5 py-0.5 rounded font-medium backdrop-blur-md flex items-center gap-1">
            <Clock className="w-2.5 h-2.5" />
            {fmtDuration(asset.duration_sec)}
          </div>
        )}
        {asset.first_downloaded_at && (
          <div
            data-testid={`delivered-badge-${asset.id}`}
            className="absolute bottom-2 left-2 bg-emerald-500/90 text-black text-[10px] px-1.5 py-0.5 rounded-full font-semibold uppercase tracking-wider backdrop-blur-md flex items-center gap-1"
            title={`Client downloaded ${new Date(asset.first_downloaded_at).toLocaleString()}`}
          >
            <CheckCircle2 className="w-2.5 h-2.5" />
            Delivered
          </div>
        )}
        {justTagged && !selectMode && (
          <div
            data-testid={`just-tagged-pip-${asset.id}`}
            className="absolute top-2 left-2 z-10 flex items-center gap-1 bg-black/70 backdrop-blur-md px-1.5 py-0.5 rounded-full"
            title={`Tagged ${new Date(asset.last_tagged_at).toLocaleString()}`}
          >
            <span className="w-1.5 h-1.5 rounded-full bg-[var(--brand-accent)] animate-pulse" />
            <span className="text-[9px] uppercase tracking-wider text-[var(--brand-accent)] font-semibold">Just tagged</span>
          </div>
        )}
      </div>

      <div className="p-2">
        <h3 className="text-sm font-medium text-zinc-100 truncate">{asset.title}</h3>
        {asset.tags?.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1.5">
            {asset.tags.slice(0, 2).map((t) => (
              <span key={t} className="text-[9px] uppercase tracking-wider text-zinc-400 bg-zinc-900/60 border border-zinc-800/60 px-1.5 py-0.5 rounded-full">
                {t}
              </span>
            ))}
            {asset.tags.length > 2 && (
              <span className="text-[9px] text-zinc-500">+{asset.tags.length - 2}</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export function AssetRow({ asset, onClick }) {
  const ready = asset.status === "ready";
  const justTagged = isRecentlyTagged(asset.last_tagged_at);
  return (
    <button
      data-testid={TID.assetCard(asset.id)}
      onClick={onClick}
      className="w-full flex items-center gap-4 px-3 py-2 border-b border-zinc-900/60 hover:bg-zinc-900/30 transition text-left"
    >
      <div className="w-32 h-[72px] flex-shrink-0 rounded overflow-hidden bg-zinc-900 relative">
        {ready && asset.thumbnail_url ? (
          <img src={asset.thumbnail_url} alt="" className="w-full h-full object-cover" onError={(e) => {
            healAssetThumbnail(asset.id, (fresh) => { e.target.src = fresh; e.target.style.display = ""; });
            e.target.style.display = "none";
          }} />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-zinc-700">
            {["uploading", "processing"].includes(asset.status) ? <div className="spinner" /> : <ImageOff className="w-4 h-4" />}
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-zinc-100 truncate flex items-center gap-2">
          <span className="truncate">{asset.title}</span>
          {justTagged && (
            <span
              data-testid={`just-tagged-pip-${asset.id}`}
              title={`Tagged ${new Date(asset.last_tagged_at).toLocaleString()}`}
              className="flex-shrink-0 flex items-center gap-1 bg-[var(--brand-accent)]/10 border border-[var(--brand-accent)]/30 px-1.5 py-0.5 rounded-full"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-[var(--brand-accent)] animate-pulse" />
              <span className="text-[9px] uppercase tracking-wider text-[var(--brand-accent)] font-semibold">Just tagged</span>
            </span>
          )}
        </div>
        {asset.description && <div className="text-xs text-zinc-500 truncate">{asset.description}</div>}
        {asset.tags?.length > 0 && (
          <div className="flex gap-1 mt-1 flex-wrap">
            {asset.tags.slice(0, 5).map((t) => (
              <span key={t} className="text-[9px] uppercase tracking-wider text-zinc-400 bg-zinc-900/60 border border-zinc-800/60 px-1.5 py-0.5 rounded-full">
                {t}
              </span>
            ))}
          </div>
        )}
      </div>
      <div className="text-xs text-zinc-500 hidden md:block">
        {asset.duration_sec > 0 ? fmtDuration(asset.duration_sec) : ""}
      </div>
      <div className="text-[10px] uppercase tracking-wider text-zinc-600 hidden md:block">
        {asset.status}
      </div>
    </button>
  );
}
